export const NAV_LINKS = [
  { label: "Overview", to: "/" },
  { label: "Agents", to: "/agents" },
  { label: "Vaults", to: "/vaults" },
  { label: "Terminal", to: "/terminal" },
  { label: "Docs", to: "/docs" },
];

export const FOOTER_LINKS = [
  ...NAV_LINKS.slice(0, 4),
  { label: "Market", to: "/market" },
  { label: "Leaderboard", to: "/leaderboard" },
  { label: "Docs", to: "/docs" },
  { label: "About", to: "/about" },
];

export const MODULES = [
  { n: "01", title: "SYNAPTIX CORE", desc: "AI agent orchestration layer coordinating mandates, signals and capital flows.", status: "LIVE CONCEPT", icon: "Cpu" },
  { n: "02", title: "SYNAPTIX VAULT", desc: "Programmable autonomous capital vaults with contract-enforced permissions.", status: "COMING SOON", icon: "Vault" },
  { n: "03", title: "AGENT REGISTRY", desc: "Discover, inspect and deploy specialized financial agents.", status: "LIVE CONCEPT", icon: "Network" },
  { n: "04", title: "GUARDIAN", desc: "AI-powered risk supervision validating every action before capital moves.", status: "LIVE CONCEPT", icon: "ShieldCheck" },
  { n: "05", title: "MEMORY", desc: "Persistent portfolio and agent intelligence across sessions and strategies.", status: "COMING SOON", icon: "Database" },
  { n: "06", title: "TERMINAL", desc: "Unified autonomous finance command center for agents, risk and capital.", status: "LIVE CONCEPT", icon: "TerminalSquare" },
];

export const PIPELINE = [
  { n: "01", title: "DEFINE", desc: "Human defines financial mandate." },
  { n: "02", title: "ANALYZE", desc: "AI agents process market and portfolio intelligence." },
  { n: "03", title: "GUARD", desc: "Risk engine checks proposed actions." },
  { n: "04", title: "EXECUTE", desc: "Approved actions interact with onchain infrastructure." },
];

export const ARCH_NODES = [
  { id: "ANALYST", desc: "Researches market conditions, fundamentals, sentiment and portfolio context." },
  { id: "TRADER", desc: "Converts strategy signals into executable trade proposals." },
  { id: "GUARDIAN", desc: "Enforces risk constraints before execution." },
  { id: "STRATEGIST", desc: "Coordinates allocation and portfolio strategy." },
  { id: "EXECUTOR", desc: "Routes approved actions to onchain infrastructure." },
];

export const ARCH_STAGES = ["USER", "SYNAPTIX CORE", "AI AGENTS", "RISK ENGINE", "VAULT", "ONCHAIN EXECUTION"];

export const HOME_AGENTS = [
  { id: "ANALYST-01", role: "Research", risk: "Low", status: "NOT DEPLOYED", spec: "Fundamental / Macro" },
  { id: "TRADER-07", role: "Execution", risk: "Medium", status: "NOT DEPLOYED", spec: "Momentum / Technical" },
  { id: "GUARDIAN-11", role: "Risk", risk: "Low", status: "NOT DEPLOYED", spec: "Exposure / Drawdown" },
  { id: "QUANT-21", role: "Strategy", risk: "Medium", status: "NOT DEPLOYED", spec: "Quantitative / Volatility" },
  { id: "MACRO-03", role: "Research", risk: "Low", status: "NOT DEPLOYED", spec: "Global / Rates" },
  { id: "ALLOCATOR-08", role: "Allocation", risk: "Medium", status: "NOT DEPLOYED", spec: "Capital Distribution" },
];

export const AGENTS = [
  { id: "ORACLE-01", name: "ORACLE", role: "Research Agent", category: "Research", risk: "Low", status: "NOT DEPLOYED", spec: "Fundamental / Macro",
    desc: "Aggregates market intelligence, fundamentals and macro context into a shared state other agents can reason over.",
    capabilities: ["Market Intelligence", "Fundamental Analysis", "Macro Context", "Sentiment Aggregation"],
    permissions: { maxPosition: 10, maxTrade: 250, maxDrawdown: 5, allowed: "Equities, ETFs" } },
  { id: "SENTIMENT-04", name: "SENTIMENT", role: "Research Agent", category: "Research", risk: "Low", status: "NOT DEPLOYED", spec: "Crowd / Narrative",
    desc: "Tracks narrative velocity and crowd positioning to flag regime shifts before they surface in price.",
    capabilities: ["Social Signal", "Narrative Tracking", "Event Detection"],
    permissions: { maxPosition: 8, maxTrade: 150, maxDrawdown: 4, allowed: "Equities" } },
  { id: "MOMENTUM-07", name: "MOMENTUM", role: "Trading Agent", category: "Trading", risk: "Medium", status: "NOT DEPLOYED", spec: "Momentum / Technical",
    desc: "Converts technical structure and momentum persistence into sized, risk-bounded trade proposals.",
    capabilities: ["Momentum", "Technical Analysis", "Signal Generation"],
    permissions: { maxPosition: 20, maxTrade: 500, maxDrawdown: 10, allowed: "Equities, ETFs" } },
  { id: "ARBITRAGE-12", name: "ARBITRAGE", role: "Trading Agent", category: "Trading", risk: "High", status: "NOT DEPLOYED", spec: "Cross-Venue / Spread",
    desc: "Detects spread dislocations across venues and proposes latency-aware capture routes.",
    capabilities: ["Spread Detection", "Route Optimization", "Execution Timing"],
    permissions: { maxPosition: 25, maxTrade: 1000, maxDrawdown: 12, allowed: "Equities, Tokenized Assets" } },
  { id: "GUARDIAN-11", name: "GUARDIAN", role: "Risk Agent", category: "Risk", risk: "Low", status: "NOT DEPLOYED", spec: "Exposure / Drawdown",
    desc: "Supervises every proposed action against mandate constraints. Nothing executes without Guardian approval.",
    capabilities: ["Exposure Control", "Drawdown Protection", "Execution Limits", "Position Validation"],
    permissions: { maxPosition: 20, maxTrade: 500, maxDrawdown: 10, allowed: "All Approved" } },
  { id: "QUANT-21", name: "QUANT", role: "Strategy Agent", category: "Allocation", risk: "Medium", status: "NOT DEPLOYED", spec: "Quantitative / Volatility",
    desc: "Runs quantitative allocation models, volatility targeting and portfolio optimization over the shared state.",
    capabilities: ["Quantitative Models", "Portfolio Optimization", "Volatility Targeting"],
    permissions: { maxPosition: 20, maxTrade: 500, maxDrawdown: 10, allowed: "Equities, ETFs" } },
  { id: "ALLOCATOR-08", name: "ALLOCATOR", role: "Allocation Agent", category: "Allocation", risk: "Medium", status: "NOT DEPLOYED", spec: "Capital Distribution",
    desc: "Distributes capital across strategies and vaults, generating rebalance proposals under liquidity constraints.",
    capabilities: ["Weighting", "Rebalance Proposals", "Liquidity Awareness"],
    permissions: { maxPosition: 30, maxTrade: 750, maxDrawdown: 10, allowed: "Vaults, ETFs" } },
  { id: "MACRO-03", name: "MACRO", role: "Macro Agent", category: "Macro", risk: "Low", status: "NOT DEPLOYED", spec: "Global / Rates",
    desc: "Maintains the macro regime model — rates, liquidity cycles and cross-asset context for the whole layer.",
    capabilities: ["Macro Regimes", "Rate Curves", "Cross-Asset Context"],
    permissions: { maxPosition: 10, maxTrade: 200, maxDrawdown: 5, allowed: "ETFs" } },
  { id: "EXECUTOR-02", name: "EXECUTOR", role: "Execution Agent", category: "Execution", risk: "Medium", status: "NOT DEPLOYED", spec: "Routing / Settlement",
    desc: "Routes Guardian-approved actions to onchain infrastructure with slippage control and settlement tracking.",
    capabilities: ["Order Routing", "Slippage Control", "Settlement Tracking"],
    permissions: { maxPosition: 20, maxTrade: 500, maxDrawdown: 10, allowed: "Onchain Venues" } },
];

export const AGENT_FILTERS = ["All", "Research", "Trading", "Risk", "Allocation", "Execution", "Macro"];

export const VAULTS = [
  { name: "ALPHA VAULT", profile: "Balanced", risk: "Medium", agent: "QUANT-21", allocation: "Multi-asset / adaptive weights", status: "COMING SOON" },
  { name: "QUANT VAULT", profile: "Systematic", risk: "Medium", agent: "MOMENTUM-07", allocation: "Momentum sleeve / volatility target", status: "COMING SOON" },
  { name: "DEFENSIVE VAULT", profile: "Capital Preservation", risk: "Low", agent: "GUARDIAN-11", allocation: "Low beta / cash overlay", status: "COMING SOON" },
  { name: "AUTONOMOUS VAULT", profile: "Adaptive", risk: "Adaptive", agent: "ALLOCATOR-08", allocation: "Agent-coordinated / dynamic", status: "COMING SOON" },
];

export const ROADMAP = [
  { phase: "PHASE 01", title: "FOUNDATION", items: ["Brand", "Architecture", "Terminal", "Agent Framework"], status: "IN DEVELOPMENT" },
  { phase: "PHASE 02", title: "INTELLIGENCE", items: ["Agent Registry", "Portfolio Brain", "Guardian", "Memory"], status: "PLANNED" },
  { phase: "PHASE 03", title: "CAPITAL", items: ["Vaults", "Strategy Engine", "Execution Layer"], status: "PLANNED" },
  { phase: "PHASE 04", title: "AUTONOMY", items: ["Multi-Agent Treasury", "Autonomous Rebalancing", "Cross-Protocol Coordination"], status: "RESEARCH" },
];

export const PRINCIPLES = [
  { n: "01", title: "Human-defined mandates", desc: "Intent, constraints and capital boundaries always originate with a human." },
  { n: "02", title: "Machine-assisted intelligence", desc: "Agents analyze, coordinate and propose. They never define the mandate." },
  { n: "03", title: "Contract-enforced permissions", desc: "Every permission is enforced onchain, not by policy documents." },
  { n: "04", title: "Transparent execution", desc: "Every action is logged, auditable and attributable to an agent and a mandate." },
  { n: "05", title: "Composable infrastructure", desc: "Agents, vaults and risk modules compose like financial primitives." },
];
